import {
  Alert,
  Button,
  Col,
  DesignTokens,
  FullWidthSpace,
  LabeledItem,
  Row,
  Space,
  Title,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useFormikContext } from "formik";
import {
  DatePickerField,
  DisplayFormErrors,
  DrawerFooterContent,
  SelectField,
  VerticalForm,
} from "melco-shared-logic";
import { Drawer as DrawerType } from "../../../../../hooks/drawer/useGenericDrawer";
import dayjs from "dayjs";
import { formatUTCDate } from "../../../../../helper/dateHelper";
import { InputNumberField } from "../../../../input/InputNumberField";
import { InitialValueType } from "./ChangeMachineLicensesChanges";

const Right = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const AlertWrapper = styled.div`
  margin-bottom: 25px;
`;

const { spacings } = DesignTokens;

type DisplayChangeMachineLicensesChangesProps = {
  referenceDrawer?: DrawerType;
  stepback: any;
  currentLicense: any;
  //callBackForVersionDisplayValue:any;
  callBackForSubscriptionCodeDisplayValue: any;
};

export const DisplayChangeMachineLicensesChanges: React.FC<
  DisplayChangeMachineLicensesChangesProps
> = ({ stepback, currentLicense, callBackForSubscriptionCodeDisplayValue }) => {
  const { t } = useTranslation();

  const { submitForm, setFieldValue, values } = useFormikContext<InitialValueType>();
  const valueSelectedInNumber = values?.reduce_license_count ?? 0;


  return (
    <>
      <div>
        <FullWidthSpace direction="vertical" size="small">
          <DisplayFormErrors />
          {currentLicense?.is_pending && <AlertWrapper>
            <Alert
              type="warning"
              style={{ width: "100%" }}
              message={t("licensemanager.changelevel.warning.message")}
              withBackground
            />
          </AlertWrapper>
          }
          <Title level={4}>
            {t("licensemanager.changelevel.form.licensedetails")}
          </Title>

          <Row gutter={[spacings.middle, 0]}>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="middle">
                <LabeledItem
                  label={("Transaction id")}
                  content={currentLicense.transaction_id}
                  fallback={t("licensemanager.changelevel.form.fallback")}
                />
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="middle">
                <LabeledItem
                  label="Number of assigned machines"
                  content={currentLicense?.license_count}
                  fallback="empty"
                />
                <LabeledItem
                  label={("Billing cycle")}
                  content={currentLicense.subscription_code}
                  fallback={t("licensemanager.changelevel.form.fallback")}
                />
                {(
                  <>
                    <LabeledItem
                      label={t("licensemanager.changelevel.form.subscription_start_date")}
                      content={
                        dayjs(
                          currentLicense?.subscription_start_date ?? ""
                        ).isValid()
                          ? formatUTCDate(
                            currentLicense?.subscription_start_date
                            , "MM/DD/YYYY")
                          : ""
                      }
                      fallback={t("global.fallback.empty")}
                    />
                    <LabeledItem
                      label={t("licensemanager.changelevel.form.renewes_on")}
                      content={
                        dayjs(currentLicense?.renews_on ?? "").isValid()
                          ? formatUTCDate(currentLicense?.renews_on, "MM/DD/YYYY")
                          : ""
                      }
                      fallback={t("global.fallback.empty")}
                    />
                  </>
                )}
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="middle">
                <LabeledItem
                  label={t("licensemanager.changelevel.form.owner")}
                  content={
                    <>
                      <Row>{currentLicense.master_user_name}</Row>
                      <Row>{currentLicense.master_name}</Row>
                    </>
                  }
                  fallback={t("licensemanager.changelevel.form.fallback")}
                />

                <LabeledItem
                  label={("Notification e-mail address")}
                  content={currentLicense.email_address}
                  fallback={t("licensemanager.changelevel.form.fallback")}
                />
                <LabeledItem
                  label={t("licensemanager.changelevel.form.language")}
                  content={
                    currentLicense.language != ""
                      ? t(`language.${currentLicense.language}` as any)
                      : ""
                  }
                  fallback={t("licensemanager.changelevel.form.fallback")}
                />
              </Space>
            </Col>
          </Row>

          <div>
            <VerticalForm>
              <Title level={4}>
                {t("licensemanager.changelevel.form.title.changes")}
              </Title>

              <InputNumberField
                name="reduce_license_count"
                min={0}
                max={currentLicense?.license_count}
                label="Reduce amount of licenses by"
                info={"This will decrease licenses from total " + currentLicense?.license_count + " to " + (currentLicense?.license_count - valueSelectedInNumber)}
                // onChange={(value) => {
                //   if (value !== null && value !== undefined) {
                //     const val = Number(value);
                //     // setReduceLicenseCount(val);

                //     const result = currentLicense?.license_count - val;
                //     console.log("Result after subtracting from 50:", result);

                //     // If you want to store this result in a state:
                //     setReduceLicenseCount(result); // You should define this with useState
                //   }
                // }}
                style={{ width: "100%" }}
              />

              <SelectField
                name="new_subscription_code"
                label={"Billing cycle"}
                options={[
                  { label: t("machinelicenses.create.form.monthly"), value: "MONTHLY" },
                  { label: t("machinelicenses.create.form.yearly"), value: "YEARLY" },
                ]}
              />
              {(
                <DatePickerField
                  label={
                    currentLicense?.renews_on
                      ? t("licensemanager.changelevel.form.renewes_on")
                      : t("licensemanager.changelevel.form.subscription_start_date")
                  }
                  name="new_sub_start_date"

                  // Disable all dates before the minimum allowed date
                  disabledDate={(current) => {
                    const today = dayjs().startOf("day");

                    if (!current) return true;

                    const checkDate = current.startOf("day");

                    // Always disable dates before today
                    if (checkDate.isBefore(today)) {
                      return true;
                    }

                    // Parse the renewal and subscription dates from the API
                    const renewalDate = currentLicense?.renews_on
                      ? dayjs(formatUTCDate(currentLicense.renews_on)).startOf("day").toDate()
                      : null;

                    const subscriptionStartDate = dayjs(
                      formatUTCDate(currentLicense.subscription_start_date)
                    ).startOf("day").toDate();

                    //  Business Logic:
                    // - If renewal_date exists, disable dates <= renewal_date
                    // - Else (renewal_date is null), use subscription_start_date
                    //   which is guaranteed to be present and NOT in the past

                    // If renewal date exists, only allow dates *after* it
                    if (renewalDate && (checkDate.isBefore(renewalDate) || checkDate.isSame(renewalDate))) {
                      return true;
                    }

                    // If no renewal date, fallback to subscription start date (always exists)
                    // Disallow selection of the same day or any earlier date
                    if (!renewalDate && (checkDate.isBefore(subscriptionStartDate) || checkDate.isSame(subscriptionStartDate))) {
                      return true;
                    }

                    //  Valid date
                    return false;
                  }}

                  // When user selects a date, store it as start of the day (local time)
                  onChange={(date, dateString) => {
                    setFieldValue(
                      "new_sub_start_date",
                      date ? dayjs(date).startOf("day").toDate() : null
                    );
                  }}
                />
              )}
            </VerticalForm>
          </div>
        </FullWidthSpace>
        <DrawerFooterContent>
          <Row>
            <Col xs={24} md={12}>
              <Button
                style={{ minWidth: "80px" }}
                key={"back-btn"}
                type="primary"
                onClick={stepback}
              >
                {t("licensemanager.buttons.back")}
              </Button>
            </Col>
            <Col
              style={{ display: "flex", justifyContent: "flex-end" }}
              xs={24}
              md={12}
            >
              <Right>
                <Button
                  style={{ minWidth: "80px" }}
                  key="add-user-button"
                  type="primary"
                  onClick={() => submitForm()}
                >
                  {t("licensemanager.buttons.next")}
                </Button>
              </Right>
            </Col>
          </Row>
        </DrawerFooterContent>
      </div>
    </>
  );
};
